import React, { useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import { FormLabel, Offcanvas, Card } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import Creatable from 'react-select/creatable'
import { FileUpload } from 'components/FileUpload'
import { useFileUpload } from 'hooks/useFileUpload'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import DatePicker from 'react-datepicker'
import { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import { RichTextFieldModal } from 'components/RichText'

import 'react-datepicker/dist/react-datepicker.css'
import './publicaciones.css'

const TIPO_VIDEO = 'video'
const TIPO_GALERIA = 'galeria'

function EditarPublicacion(props) {
    const { publicacionOriginal, etiquetasExistentes, config, guardar, setQueMostrar } = props

    registerLocale('es', es)

    const esBorrador = publicacionOriginal.borrador === '1' ||
        publicacionOriginal.borrador === 1 ||
        publicacionOriginal.borrador === true

    const [etiquetas, setEtiquetas] = useState(
        publicacionOriginal.etiquetas?.map(elem => ({
            label: elem.nombre,
            value: elem.id
        })) || []
    )

    const [datos, setDatos] = useState({
        id: publicacionOriginal.id,
        borrador: esBorrador,
        tipo: publicacionOriginal.tipo,
        titulo: publicacionOriginal.titulo || '',
        copete: publicacionOriginal.copete || '',
        contenido: publicacionOriginal.contenido || '',
        videoUrl: publicacionOriginal.videoUrl || '',
        autor: publicacionOriginal.autor || '',
        fecha: publicacionOriginal.fecha ? new Date(publicacionOriginal.fecha) : new Date()
    })

    // Imagenes de galeria existentes
    const [imagenesGaleria, setImagenesGaleria] = useState(
        publicacionOriginal.imagenes || []
    )
    const [imagenesAEliminar, setImagenesAEliminar] = useState([])

    const [erroresValidacion, setErroresValidacion] = useState({})
    const [videoPreview, setVideoPreview] = useState(
        extraerYouTubeId(publicacionOriginal.videoUrl)
    )

    // Hook para imagen destacada
    const fileUploadThumbnail = useFileUpload({
        maxFiles: 1,
        maxFileSize: 5000000,
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
        endpoint: 'a/multimedia',
        fieldName: 'foto',
        uploadOnSelect: false
    })

    // Hook para nuevas imagenes de galeria
    const fileUploadGaleria = useFileUpload({
        maxFiles: (config.maxImagenesGaleria || 10) - imagenesGaleria.length,
        maxFileSize: 5000000,
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
        endpoint: 'a/multimedia',
        fieldName: 'foto',
        uploadOnSelect: false
    })

    const opciones = etiquetasExistentes.map(elem => ({
        label: elem.nombre,
        value: elem.id
    }))

    function extraerYouTubeId(url) {
        if (!url) return null

        const patterns = [
            /youtube\.com\/watch\?v=([^&]+)/,
            /youtube\.com\/embed\/([^?]+)/,
            /youtu\.be\/([^?]+)/,
            /youtube\.com\/v\/([^?]+)/
        ]

        for (const pattern of patterns) {
            const match = url.match(pattern)
            if (match) return match[1]
        }

        if (url.length === 11) return url
        return null
    }

    const handleVideoUrlChange = (url) => {
        setDatos(prev => ({ ...prev, videoUrl: url }))
        setVideoPreview(extraerYouTubeId(url))
    }

    const handleClose = () => {
        setQueMostrar('')
    }

    const handleProcesar = async (e) => {
        e.preventDefault()

        if (!validarDatos()) return

        // Subir imagen destacada si existe nueva
        let archivoId = null
        if (fileUploadThumbnail.selectedFiles.length > 0) {
            const resultados = await fileUploadThumbnail.uploadFiles()
            const exitosos = resultados.filter(r => r.success)
            if (exitosos.length > 0) {
                archivoId = exitosos[0].id
            }
        }

        // Subir nuevas imagenes de galeria
        let imagenesNuevasIds = []
        if (datos.tipo === TIPO_GALERIA && fileUploadGaleria.selectedFiles.length > 0) {
            const resultados = await fileUploadGaleria.uploadFiles()
            imagenesNuevasIds = resultados.filter(r => r.success).map(r => r.id)
        }

        // Verificar etiquetas eliminadas
        const etiquetasIds = etiquetas.map(elem => elem.value)
        const eliminarEtiquetasIds = []
        publicacionOriginal.etiquetas?.forEach(etiqueta => {
            if (!etiquetasIds.includes(etiqueta.id)) {
                eliminarEtiquetasIds.push(etiqueta.id)
            }
        })

        // Preparar datos
        const dataEnviar = {
            ...datos,
            etiquetas: etiquetas.map(elem => elem.label)
        }

        if (eliminarEtiquetasIds.length > 0) {
            dataEnviar.eliminarEtiquetasIds = eliminarEtiquetasIds
        }

        if (archivoId) {
            dataEnviar.archivoId = archivoId
        }

        // Datos de galeria
        if (datos.tipo === TIPO_GALERIA) {
            dataEnviar.imagenesGaleria = {
                nuevas: imagenesNuevasIds,
                eliminar: imagenesAEliminar,
                orden: imagenesGaleria.map((img, idx) => ({
                    id: img.id,
                    orden: idx + 1
                }))
            }
        }

        guardar(dataEnviar)
        handleClose()
    }

    const validarDatos = () => {
        const nuevosErrores = {}

        if (!datos.titulo || datos.titulo.trim() === '') {
            nuevosErrores.titulo = 'El titulo es requerido'
        }

        if (!datos.autor || datos.autor.trim() === '') {
            nuevosErrores.autor = 'El autor es requerido'
        }

        if (datos.tipo === TIPO_VIDEO && !datos.videoUrl) {
            nuevosErrores.videoUrl = 'La URL del video es requerida'
        }

        if (datos.tipo === TIPO_VIDEO && datos.videoUrl && !extraerYouTubeId(datos.videoUrl)) {
            nuevosErrores.videoUrl = 'URL de YouTube invalida'
        }

        setErroresValidacion(nuevosErrores)
        return Object.keys(nuevosErrores).length === 0
    }

    const handleChange = (value, key) => {
        setDatos(prev => ({ ...prev, [key]: value }))

        if (erroresValidacion[key]) {
            setErroresValidacion(prev => ({ ...prev, [key]: null }))
        }
    }

    // Eliminar imagen de galeria
    const handleEliminarImagen = (imagen) => {
        setImagenesAEliminar(prev => [...prev, imagen.id])
        setImagenesGaleria(prev => prev.filter(img => img.id !== imagen.id))
    }

    // Reordenar galeria con drag & drop
    const handleDragEnd = (result) => {
        if (!result.destination) return

        const items = Array.from(imagenesGaleria)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)

        setImagenesGaleria(items)
    }

    const isLoading = fileUploadThumbnail.isLoading || fileUploadGaleria.isLoading
    const espacioDisponible = (config.maxImagenesGaleria || 10) - imagenesGaleria.length

    return (
        <Offcanvas show={true} onHide={handleClose} placement="end" className="publicacion-panel">
            <PanelHeader
                onClick={handleClose}
                iconClass="fa-arrow-left"
                btnText="Atras"
                text={`Editar ${datos.tipo === TIPO_VIDEO ? 'Video' : 'Galeria'}`}
            />
            <Offcanvas.Body id="editar-publicacion">
                <Form onSubmit={handleProcesar}>
                    {/* Tipo (solo lectura) */}
                    <Alert variant="info" className="mb-3">
                        <i className={`fa fa-${datos.tipo === TIPO_VIDEO ? 'play-circle' : 'images'} me-2`}></i>
                        Tipo: <strong>{datos.tipo === TIPO_VIDEO ? 'Video' : 'Galeria de Imagenes'}</strong>
                    </Alert>

                    {/* Estado */}
                    <Card className="mb-3">
                        <Card.Body>
                            <Form.Group controlId="pub-borrador">
                                <Form.Check
                                    type="switch"
                                    label={datos.borrador ? 'Borrador' : 'Publicada'}
                                    checked={!datos.borrador}
                                    onChange={(e) => handleChange(!e.target.checked, 'borrador')}
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    {/* Datos basicos */}
                    <Card className="mb-3">
                        <Card.Header>
                            <i className="fa fa-edit me-2"></i>
                            Informacion General
                        </Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3" controlId="pub-titulo">
                                <FormLabel>Titulo <span className="text-danger">*</span></FormLabel>
                                <Form.Control
                                    value={datos.titulo}
                                    onChange={(e) => handleChange(e.target.value, 'titulo')}
                                    isInvalid={!!erroresValidacion.titulo}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {erroresValidacion.titulo}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="pub-etiquetas">
                                <FormLabel>Etiquetas</FormLabel>
                                <Creatable
                                    defaultValue={etiquetas}
                                    onChange={setEtiquetas}
                                    options={opciones}
                                    isMulti={true}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="pub-copete">
                                <FormLabel>Copete / Resumen</FormLabel>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={datos.copete}
                                    onChange={(e) => handleChange(e.target.value, 'copete')}
                                />
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="pub-autor">
                                        <FormLabel>Autor <span className="text-danger">*</span></FormLabel>
                                        <Form.Control
                                            value={datos.autor}
                                            onChange={(e) => handleChange(e.target.value, 'autor')}
                                            isInvalid={!!erroresValidacion.autor}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {erroresValidacion.autor}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="pub-fecha">
                                        <FormLabel>Fecha</FormLabel>
                                        <DatePicker
                                            selected={datos.fecha}
                                            onChange={(fecha) => handleChange(fecha, 'fecha')}
                                            showTimeSelect
                                            locale="es"
                                            dateFormat="dd/MM/yyyy HH:mm"
                                            className="form-control"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Video */}
                    {datos.tipo === TIPO_VIDEO && (
                        <Card className="mb-3">
                            <Card.Header>
                                <i className="fa fa-play-circle me-2"></i>
                                Video de YouTube
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3" controlId="pub-videoUrl">
                                    <FormLabel>URL del Video <span className="text-danger">*</span></FormLabel>
                                    <Form.Control
                                        value={datos.videoUrl}
                                        onChange={(e) => handleVideoUrlChange(e.target.value)}
                                        isInvalid={!!erroresValidacion.videoUrl}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {erroresValidacion.videoUrl}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {videoPreview && (
                                    <div className="video-preview mb-3">
                                        <div className="ratio ratio-16x9">
                                            <iframe
                                                src={`https://www.youtube.com/embed/${videoPreview}`}
                                                title="Preview"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    </div>
                                )}

                                {/* Imagen destacada actual */}
                                {publicacionOriginal.imagenDestacada && (
                                    <div className="imagen-actual mb-3">
                                        <FormLabel>Imagen destacada actual</FormLabel>
                                        <img
                                            src={process.env.REACT_APP_API_URL + publicacionOriginal.imagenDestacada.url}
                                            alt="Thumbnail"
                                            className="img-thumbnail"
                                            style={{ maxHeight: '150px' }}
                                        />
                                    </div>
                                )}

                                <FileUpload
                                    title="Nueva imagen destacada (opcional)"
                                    accept=".png, .jpg, .jpeg, .webp"
                                    buttonText="Elegir"
                                    buttonTextSingular="imagen"
                                    updateFiles={fileUploadThumbnail.handleSelectFiles}
                                    showPreview={true}
                                />
                            </Card.Body>
                        </Card>
                    )}

                    {/* Galeria */}
                    {datos.tipo === TIPO_GALERIA && (
                        <Card className="mb-3">
                            <Card.Header>
                                <i className="fa fa-images me-2"></i>
                                Galeria de Imagenes
                                <Badge bg="secondary" className="ms-2">
                                    {imagenesGaleria.length}/{config.maxImagenesGaleria || 10}
                                </Badge>
                            </Card.Header>
                            <Card.Body>
                                {/* Imagenes existentes con drag & drop */}
                                {imagenesGaleria.length > 0 && (
                                    <div className="mb-3">
                                        <FormLabel>Imagenes actuales (arrastra para reordenar)</FormLabel>
                                        <DragDropContext onDragEnd={handleDragEnd}>
                                            <Droppable droppableId="galeria" direction="horizontal">
                                                {(provided) => (
                                                    <div
                                                        {...provided.droppableProps}
                                                        ref={provided.innerRef}
                                                        className="galeria-grid"
                                                    >
                                                        {imagenesGaleria.map((img, index) => (
                                                            <Draggable
                                                                key={img.id.toString()}
                                                                draggableId={img.id.toString()}
                                                                index={index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={`galeria-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                                                    >
                                                                        <img
                                                                            src={process.env.REACT_APP_API_URL + img.url}
                                                                            alt={`Imagen ${index + 1}`}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="btn-eliminar"
                                                                            onClick={() => handleEliminarImagen(img)}
                                                                        >
                                                                            <i className="fa fa-times"></i>
                                                                        </button>
                                                                        <span className="orden-badge">{index + 1}</span>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                    </div>
                                )}

                                {/* Agregar nuevas imagenes */}
                                {espacioDisponible > 0 && (
                                    <div className="mt-3">
                                        <FileUpload
                                            title={`Agregar imagenes (${espacioDisponible} disponibles)`}
                                            accept=".png, .jpg, .jpeg, .webp"
                                            buttonText="Elegir"
                                            buttonTextSingular="imagenes"
                                            multiple={true}
                                            updateFiles={fileUploadGaleria.handleSelectFiles}
                                            showPreview={true}
                                        />
                                    </div>
                                )}

                                {espacioDisponible === 0 && (
                                    <Alert variant="warning">
                                        Has alcanzado el limite maximo de {config.maxImagenesGaleria || 10} imagenes.
                                        Elimina alguna para poder agregar mas.
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Contenido */}
                    <Card className="mb-3">
                        <Card.Header>
                            <i className="fa fa-align-left me-2"></i>
                            Contenido
                        </Card.Header>
                        <Card.Body>
                            <RichTextFieldModal
                                value={datos.contenido}
                                onChange={(contenido) => handleChange(contenido, 'contenido')}
                                label=""
                            />
                        </Card.Body>
                    </Card>

                    {/* Info adicional */}
                    <Card className="mb-3 bg-light">
                        <Card.Body>
                            <small className="text-muted">
                                <i className="fa fa-info-circle me-1"></i>
                                <strong>ID:</strong> {datos.id}
                            </small>
                        </Card.Body>
                    </Card>
                </Form>
            </Offcanvas.Body>

            <PanelFooter>
                <Button
                    variant="outline-secondary"
                    onClick={handleClose}
                    className="me-2"
                    disabled={isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleProcesar}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <><i className="fa fa-spinner fa-spin me-2"></i> Guardando...</>
                    ) : (
                        <><i className="fa fa-save me-2"></i> Guardar Cambios</>
                    )}
                </Button>
            </PanelFooter>
        </Offcanvas>
    )
}

export default EditarPublicacion
